import React, {FunctionComponent, useEffect, useState} from "react";
import IconTooltip from "../icon/IconTooltip";
import TagNew from "../TagNew";
import FlexContent, {FlexContentAlignment, FlexContentDirection, FlexContentSpacing} from "../flex-content/FlexContent";
import clsx from "clsx";
import {Color} from "../../../utils/color";
import {useTranslation} from "react-i18next";
import {TranslationLibFile} from "../../../utils/locale";
import {getValidityErrorKey} from "../../../utils/input";

export type FieldBlockProps = {
    children: React.ReactNode;
    label?: string;
    name?: string;
    required?: boolean;
    tooltip?: string;
    actions?: React.ReactNode;
    isNew?: boolean;
    inline?: boolean;
    content?: {
        direction?: FlexContentDirection;
        spacing?: FlexContentSpacing;
        noFullWidth?: boolean;
    };
    error?: string;
};

const FieldBlock: FunctionComponent<FieldBlockProps> = ({children, label, name, required, tooltip, actions, isNew, inline, content, error}) => {
    const {t} = useTranslation(TranslationLibFile.COMMON);
    const [fieldError, setFieldError] = useState("");
    const [isShowFieldError, setShowFieldError] = useState(false);
    const actionList = (Array.isArray(actions) ? actions : [actions]).filter((it) => !!it);
    const childrenList = (Array.isArray(children) ? children : [children]).filter((it) => !!it);

    useEffect(() => {
        if (!required) {
            setFieldError("");
            setShowFieldError(false);
        }
    }, [required]);

    if (!childrenList.length) {
        return <></>;
    }

    const handleInvalid = (e: React.FormEvent<HTMLObjectElement>) => {
        const element = e.target as HTMLObjectElement;
        if (element?.form?.noValidate) {
            const error = t(getValidityErrorKey(element.validity));
            setFieldError(error);
            setShowFieldError(!!error);
        }
    };

    const handleInput = (e: React.FormEvent<HTMLObjectElement>) => {
        isShowFieldError && handleInvalid(e);
    };

    return (
        <div className={clsx("field-block", {"field-block--inline": inline}, {"field-block--error": isShowFieldError})}>
            {(label || !!actionList.length) &&
                <div className="field-block__title">
                    {label &&
                        <label htmlFor={name}>
                            <span>{label}{required && " *"}</span>
                            {tooltip && <IconTooltip icon={{name: "help"}} text={tooltip} cssClass="field-block__title__tooltip"/>}
                            {isNew && <TagNew cssClass="field-block__title__new-tag"/>}
                            {error && <IconTooltip icon={{name: "error", outlined: true, colorIcon: Color.DANGER_MAIN}} text={error} cssClass="field-block__title__tooltip"/>}
                        </label>
                    }
                    {actionList &&
                        <div className="field-block__title__actions">
                            {actionList.map((action, index) =>
                                <React.Fragment key={index}>
                                    {action}
                                </React.Fragment>
                            )}
                        </div>
                    }
                </div>
            }
            <FlexContent direction={FlexContentDirection.COLUMN} cssClass="field-block__content-wrapper">
                <FlexContent
                    direction={content?.direction || FlexContentDirection.ROW}
                    spacing={content?.spacing || FlexContentSpacing.XSMALL}
                    alignment={content?.direction === FlexContentDirection.COLUMN ? FlexContentAlignment.INITIAL : FlexContentAlignment.CENTER}
                    cssClass={clsx("field-block__content", {"field-block__content--no-full-width": content?.noFullWidth})}
                >
                    {childrenList.map((element, index) =>
                        <React.Fragment key={index}>
                            {typeof element === "string" ?
                                <span>{element}</span> :
                                React.cloneElement(element as React.ReactElement, {
                                    name: name || element.props.name,
                                    required: required || element.props.required,
                                    onInvalid: handleInvalid,
                                    onInput: handleInput
                                })
                            }
                        </React.Fragment>
                    )}
                </FlexContent>
                {(isShowFieldError && fieldError) &&
                    <div className="field-block__error">{fieldError}</div>
                }
            </FlexContent>
        </div>
    );
};

export default FieldBlock;
